import { Injectable, inject } from '@angular/core';
import { UsersService } from '@app/shared/services/entities/users.service';

/**
 * Alcance del usuario: hospitales (sitios) y especialidades asignados en
 * Usuarios → Parámetros (tabla `customparams`). El backend los devuelve en
 * `users/id/roles` como `place` y `specialty`.
 *
 * Regla: `null` = SIN restricción, el usuario ve todo. Solo cuando hay ids
 * asignados se filtra.
 */
export interface UserScope {
  places: string[] | null;
  specialties: string[] | null;
  /** Nombres de rol normalizados (MAYÚSCULAS, sin acentos). Solo para mostrar
   *  u ocultar botones: el API vuelve a validar el rol. */
  roles?: string[];
}

/** Normaliza lo que venga (null, id suelto, lista) a lista no vacía o null. */
export function scopeIds(value: any): string[] | null {
  if (value === null || value === undefined || value === '') return null;
  const list = Array.isArray(value) ? value : [value];
  const clean = list.filter(v => v !== null && v !== undefined && v !== '').map(v => String(v));
  return clean.length ? clean : null;
}

/** ¿El id entra en el alcance? Sin alcance, todo entra. `allowEmpty`: un
 *  registro sin ese dato (p. ej. borrador sin especialidad) también entra. */
export function inScope(scope: string[] | null, id: any, allowEmpty = true): boolean {
  if (!scope) return true;
  if (id === null || id === undefined || id === '') return allowEmpty;
  return scope.includes(String(id));
}

/** Recorta un catálogo ({id, ...}) al alcance. */
export function restrictToScope<T extends { id?: any }>(list: T[], scope: string[] | null): T[] {
  if (!scope) return list;
  return (list ?? []).filter(item => item && scope.includes(String(item.id)));
}

@Injectable({ providedIn: 'root' })
export class UserScopeService {
  private usersService = inject(UsersService);
  private cache: { userId: string; scope: UserScope } | null = null;

  /** Construye el alcance a partir de la respuesta de `users/id/roles`. */
  fromContext(context: any): UserScope {
    // Igual que el API: sin hospitales asignados no se ve ningún hospital;
    // solo el administrador ve todos (para asignar los parámetros).
    const roles = (Array.isArray(context?.roles) ? context.roles : [context?.roles])
      .map((r: any) => String(r ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase());
    const isAdmin = roles.includes('ADMINISTRADOR EPIONE');
    const places = scopeIds(context?.place);
    return { places: places ?? (isAdmin ? null : []), specialties: scopeIds(context?.specialty), roles: roles.filter(Boolean) };
  }

  /** Alcance del usuario en sesión (se consulta una vez por usuario). Si
   *  falla la consulta, no se restringe nada. */
  async load(force = false): Promise<UserScope> {
    const userId = sessionStorage.getItem('userId') ?? '';
    if (!force && this.cache && this.cache.userId === userId) return this.cache.scope;
    try {
      const response: any = await this.usersService.getRolNameByUserId({ id: userId });
      const scope = this.fromContext(response?.data?.[0]);
      this.cache = { userId, scope };
      return scope;
    } catch {
      return { places: null, specialties: null };
    }
  }
}
